/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
// frontend/src/components/Search/CompactNearInput.jsx
// Compact NEAR search input (CBETA style - replaces large NearSearchForm)

import { useState } from 'react';
import { Box, TextField, Typography, IconButton, Tooltip, InputAdornment } from '@mui/material';
import { Search as SearchIcon, HelpOutline as HelpIcon } from '@mui/icons-material';

/**
 * CompactNearInput - Compact NEAR proximity search input
 *
 * Layout: [Keyword1] NEAR [distance] [Keyword2] [Search]
 * - Uses number input instead of slider (70% less vertical space)
 * - Range: 5-50, default: 10
 *
 * @param {Object} nearState - { keyword1, keyword2, distance }
 * @param {Function} onNearStateChange - Callback when state changes
 * @param {Function} onSearch - Callback to execute search
 * @param {boolean} disabled - Whether inputs are disabled
 */
const CompactNearInput = ({ nearState, onNearStateChange, onSearch, disabled = false }) => {
  const [showHelp, setShowHelp] = useState(false);

  const { keyword1 = '', keyword2 = '', distance = 10 } = nearState;

  /**
   * Handle input changes
   */
  const handleChange = (field, value) => {
    onNearStateChange({
      ...nearState,
      [field]: value,
    });
  };

  /**
   * Handle distance change with validation
   */
  const handleDistanceChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 5 && value <= 50) {
      handleChange('distance', value);
    } else if (e.target.value === '') {
      handleChange('distance', 10);
    }
  };

  /**
   * Handle search submission
   */
  const handleSubmit = (e) => {
    e?.preventDefault();
    if (keyword1.trim() && keyword2.trim()) {
      const query = `${keyword1.trim()} NEAR[${distance}] ${keyword2.trim()}`;
      onSearch(query);
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const canSearch = keyword1.trim() && keyword2.trim();

  return (
    <Box>
      {/* Compact input row */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Keyword 1 */}
        <TextField
          size="small"
          placeholder="關鍵字一"
          value={keyword1}
          onChange={(e) => handleChange('keyword1', e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 120 }}
          autoComplete="off"
        />

        {/* NEAR label + distance */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: 'action.hover',
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            NEAR
          </Typography>
          <TextField
            size="small"
            type="number"
            value={distance}
            onChange={handleDistanceChange}
            disabled={disabled}
            inputProps={{
              min: 5,
              max: 50,
              style: { textAlign: 'center', width: 40 },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            字內
          </Typography>
        </Box>

        {/* Keyword 2 */}
        <TextField
          size="small"
          placeholder="關鍵字二"
          value={keyword2}
          onChange={(e) => handleChange('keyword2', e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 120 }}
          autoComplete="off"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="說明">
                  <IconButton size="small" onClick={() => setShowHelp(!showHelp)} edge="end">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        {/* Search button */}
        <IconButton
          type="submit"
          color="primary"
          disabled={!canSearch || disabled}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '&.Mui-disabled': {
              backgroundColor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          <SearchIcon />
        </IconButton>
      </Box>

      {/* Help text (collapsible) */}
      {showHelp && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            backgroundColor: 'info.lighter',
            borderRadius: 1,
            borderLeft: '3px solid',
            borderColor: 'info.main',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>鄰近搜尋</strong>可搜尋兩個關鍵字在指定距離內同時出現的文獻。
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            範例：「佛法 NEAR[10] 正見」可搜尋這兩個詞在 10 字以內的文獻。
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CompactNearInput;
